import { Plus, Repeat, Trash2 } from "lucide-react";
import type { RecurringException } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Notify } from "@/components/ui/notify";
import { RecurrenceRuleRow } from "./RecurrenceRuleRow";

interface Props {
  value: RecurringException[];
  onChange: (rules: RecurringException[]) => void;
  disabled?: boolean;
  /** `card` na tela da organização; `inline` dentro da sheet da linha. */
  variant?: "card" | "inline";
}

const NEW_RULE: RecurringException = {
  freq: "MONTHLY",
  weekday: 1,
  ordinal: 1,
  kind: "CLOSED",
};

// REGRAS RECORRENTES.
// "Toda primeira segunda do mês, manutenção" em vez de doze exceções datadas por ano, redigitadas
// todo dezembro. Três formas, e apenas estas; o que não cabe nelas continua cabendo numa exceção
// datada, que é o caso geral.
// COMPONENTE PRÓPRIO, e não um enxerto no `<ExceptionsList>` (research): as três formas têm
// campos diferentes entre si e nenhum em comum com uma exceção datada, mais a prévia de ocorrências.
// Enfiar isso no editor de datas transformaria um componente simples e compartilhado por três telas
// num que precisa saber de tudo.
export function RecurrenceSection({ value, onChange, disabled, variant = "card" }: Props) {
  const patch = (i: number, next: RecurringException) =>
    onChange(value.map((r, idx) => (idx === i ? next : r)));
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));

  const body = (
    <div className="space-y-3">
      {value.length === 0 ? (
        <Notify.Empty
          variant="inline"
          title="Nenhuma regra recorrente"
          description="Use uma regra para o que se repete — manutenção mensal, domingo fechado, uma data fixa todo ano. Datas isoladas continuam entrando como exceção."
        />
      ) : (
        <ul className="space-y-2">
          {value.map((rule, i) => (
            <li key={i}>
              <RecurrenceRuleRow
                rule={rule}
                disabled={disabled}
                onChange={(next) => patch(i, next)}
                onRemove={() => remove(i)}
              />
            </li>
          ))}
        </ul>
      )}

      {disabled ? null : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange([...value, { ...NEW_RULE }])}
        >
          <Plus className="mr-1 h-3.5 w-3.5" aria-hidden /> Regra
        </Button>
      )}
    </div>
  );

  if (variant === "inline") {
    return (
      <div className="space-y-2">
        <Label className="text-[13px] font-medium">Regras recorrentes</Label>
        {body}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Repeat className="h-4 w-4 text-text-tertiary" aria-hidden />
          Regras recorrentes
        </CardTitle>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}

export { Trash2 };
